/**
 * UtilityLayout — wraps "utility" pages (/profile, /calendar, /help)
 * with the correct portal sidebar based on the user's customer_type.
 *
 * These pages live outside any portal sub-router but must still render
 * inside the full shell (header + sidebar + main).
 */

import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { PortalLayout } from './PortalLayout'
import { PMISidebar } from '@/portals/pmi/PMISidebar'
import { ConsulenteSidebar } from '@/portals/consulente/ConsulenteSidebar'
import { RicercatoreSidebar } from '@/portals/ricercatore/RicercatoreSidebar'
import { AssociazioneSidebar } from '@/portals/associazione/AssociazioneSidebar'
import { EnteSidebar } from '@/portals/ente_pubblico/EnteSidebar'
import { MediaSidebar } from '@/portals/media/MediaSidebar'
import { OsservatorioSidebar } from '@/portals/osservatorio/OsservatorioSidebar'
import { AdminSidebar } from '@/portals/admin/AdminSidebar'

interface UtilityLayoutProps {
  children: React.ReactNode
}

export function UtilityLayout({ children }: UtilityLayoutProps) {
  const { user } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const close = () => setSidebarOpen(false)
  const props = { isOpen: sidebarOpen, onClose: close }

  const sidebar = (() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      return <AdminSidebar {...props} />
    }
    switch (user?.customer_type) {
      case 'pmi':
        return <PMISidebar {...props} />
      case 'consultant':
        return <ConsulenteSidebar {...props} />
      case 'research':
        return <RicercatoreSidebar {...props} />
      case 'association':
        return <AssociazioneSidebar {...props} />
      case 'public_body':
        return <EnteSidebar {...props} />
      case 'media':
        return <MediaSidebar {...props} />
      case 'observatory':
        return <OsservatorioSidebar {...props} />
      default:
        return <PMISidebar {...props} />
    }
  })()

  return (
    <PortalLayout sidebar={sidebar} onMenuClick={() => setSidebarOpen((v) => !v)}>
      {children}
    </PortalLayout>
  )
}
